// src/components/FilterHeader.js
import React, { useEffect, useState, useContext, useRef } from 'react';
import { useLoadScript } from '@react-google-maps/api';

import { FilterContext }    from '../context/FilterContext';
import { ScrollContext }    from '../context/ScrollContext';
import { ModalContext }     from '../context/ModalContext';
import { TabContext }       from '../context/TabContext';
import { LocationContext, DEFAULT_COORDS } from '../context/LocationContext';
import ModalLocation        from './ModalLocation';
import './FilterHeader.css';

const libraries = ['places'];

export default function FilterHeader({ onHide }) {
  const { filters, setFilters }     = useContext(FilterContext);
  const { homeScrollPosition }      = useContext(ScrollContext);
  const { openModal, closeModal, modalStacks } = useContext(ModalContext);
  const { coords: locCoords, overrideLocation } = useContext(LocationContext);
  const { activeTab }               = useContext(TabContext);

  const headerRef = useRef(null);
  const didMountRef = useRef(false);

  // estados locales
  const [localSpecies, setLocalSpecies]   = useState(filters.species);
  const [localSex, setLocalSex]           = useState(filters.sex);
  const [localLocation, setLocalLocation] = useState(filters.location);
  const [cityLabel, setCityLabel]         = useState('Ubicación');

  // cargamos Google Maps
  const { isLoaded: mapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  const getCityName = address => {
    if (!address) return '';
    const parts = address.split(',');
    return parts.length >= 3
      ? parts[parts.length - 3].trim()
      : parts[0].trim();
  };

  // sincronizar con filtros
  useEffect(() => {
    setLocalSpecies(filters.species);
    setLocalSex(filters.sex);
    setLocalLocation(filters.location);
  }, [filters]);

  // calcular etiqueta ciudad
  useEffect(() => {
    const address = localLocation?.address;
    if (address) {
      setCityLabel(getCityName(address) || 'Ubicación');
      return;
    }
    if (
      locCoords.lat === DEFAULT_COORDS.lat &&
      locCoords.lng === DEFAULT_COORDS.lng
    ) {
      setCityLabel('Madrid');
      return;
    }
    if (!mapsLoaded || !window.google?.maps) {
      setCityLabel('Cargando…');
      return;
    }
    setCityLabel('Mi ubicación actual');
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: locCoords }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setCityLabel(getCityName(results[0].formatted_address) || 'Mi ubicación actual');
      }
    });
  }, [localLocation?.address, locCoords, mapsLoaded]);

  // cerrar al click fuera salvo si hay modal
  useEffect(() => {
    function onClickOutside(e) {
      if (modalStacks[activeTab]?.length > 0) return;
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        onHide?.();
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [onHide, modalStacks, activeTab]);

  // cerrar al cambiar pestaña
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (activeTab !== 'home') onHide?.();
  }, [activeTab, onHide]);

  const handleOpenLocationModal = () => {
    const initialLoc = localLocation?.address
      ? localLocation
      : { address: 'Ubicación', lat: locCoords.lat, lng: locCoords.lng };
    openModal(
      <ModalLocation
        initialLocation={initialLoc}
        onAccept={newLocation => {
          setLocalLocation(newLocation);
          closeModal('home');
        }}
        onCancel={() => closeModal('home')}
        hideRange={false}
      />,
      { tab: 'home', disableSwipe: true, overlayClassName: 'pet-detail-overlay' }
    );
  };

  const handleApply = () => {
    // 1) Actualizamos el filtro global (para LikesPage)
    setFilters({
      species: localSpecies,
      sex:     localSex,
      location: localLocation
    });

    // 2) Y actualizamos coords globales de geolocalización (para HomePage)
    if (localLocation?.lat != null && localLocation?.lng != null) {
      overrideLocation({
        lat: localLocation.lat,
        lng: localLocation.lng
      });
    }

    // 3) Cerramos el header
    onHide?.();
  };

  // ocultar al desplazarse
  const lastScroll = useRef(homeScrollPosition);
  useEffect(() => {
    if (
      homeScrollPosition > 50 &&
      homeScrollPosition > lastScroll.current
    ) {
      onHide?.();
    }
    lastScroll.current = homeScrollPosition;
  }, [homeScrollPosition, onHide]);

  const toggleSpecies = s => {
    if (localSpecies === s) {
      setLocalSpecies(null);
      setLocalSex(null);
    } else {
      setLocalSpecies(s);
      setLocalSex(null);
    }
  };
  const toggleSex = s => {
    if (!localSpecies) return;
    setLocalSex(prev => (prev === s ? null : s));
  };

  const locationConfigured = !!(localLocation?.lat && localLocation?.lng);

  return (
    <div className="filter-header" ref={headerRef}>
      {/* Columna izquierda */}
      <div className="filter-left">
        <div className="filter-row">
          <button className={localSpecies==='perro'?'selected':''} onClick={()=>toggleSpecies('perro')}>Perro</button>
          <button className={localSpecies==='gato' ?'selected':''} onClick={()=>toggleSpecies('gato')}>Gato</button>
        </div>
        <div className="filter-row">
          <button className={localSex==='macho'?'selected':''} onClick={()=>toggleSex('macho')}>Macho</button>
          <button className={localSex==='hembra'?'selected':''} onClick={()=>toggleSex('hembra')}>Hembra</button>
        </div>
      </div>

      {/* Columna derecha con dos filas alineadas al margen */}
      <div className="filter-actions">
        <button
          className="filter-button location-button"
          onClick={handleOpenLocationModal}
          style={{
            backgroundColor: locationConfigured?'#66B3FF':'#eee',
            color:           locationConfigured?'#fff':'#0093F5'
          }}
        >
          {cityLabel}
        </button>
        <button
          className="filter-button apply-button"
          onClick={handleApply}
        >
          Aplicar
        </button>
      </div>
    </div>
  );
}
